import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { useMutation } from '@apollo/client/react'
import { formValueSelector } from 'redux-form'
// hooks
import { useRowOffset, useRowsHeight } from '../../../hooks'
import { useCurrentForm, useFormValueUpdate, useSaveFieldValue, useShowModal } from 'hooks/index'
// components
import FieldContent from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/FieldContent'
import HistoryButton from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/RiskTemplateField/components/HistoryButton'
import ControlButtons from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/ControlButtons'
import InlineFunctions from './components/InlineFunctions'
// graphql
import { FIELD_VALUE_SET_ARCHIVED_MUTATION } from 'graphql/objects/mutations'
// types
import { Forms, Modals } from 'constants/index'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskViewContext } from 'containers/Risks/RiskView/Risk/layers'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// views
import { LinkIcon, OuterWrapper, RowWrapper } from './views'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

interface RowProps {
  bg?: string
  name: string
  deleteRow: () => void
  parentArchived: boolean
}

const templateSelector = formValueSelector(Forms.RISK_TEMPLATE_FORM)

/** Render content for fieldLink row field */

const Row: React.FC<RowProps> = ({ bg, name, deleteRow, parentArchived }) => {
  const router = useRouter()

  const [archiveRow] = useMutation(FIELD_VALUE_SET_ARCHIVED_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_RISK_MODAL })

  const { field, linked, fieldLink } = useCurrentForm()

  const { riskSelector } = React.useContext(RiskViewContext)
  const { isRisksRegister, riskTemplate, deleteFieldValues } = React.useContext(RiskTemplateAPIContext)
  const { risk, riskRefetch, showArchivedForms, groupedFormsPermissions, linkedFormsPermissions } =
    React.useContext(RiskAPIContext)

  const value = useSelector((state) => (riskSelector || templateSelector)(state, name))

  useFormValueUpdate(name, value)
  useSaveFieldValue({ name, field, value })

  const { ref } = useRowsHeight({ rowId: value?.fieldValueId, groupId: value?.fieldValueId })

  const offset = useRowOffset({
    rowId: value?.fieldValueId,
    groupId: value?.fieldValueId
  })

  const fieldName = `${name}.value`

  const linkedFormPermissions = linkedFormsPermissions?.find(({ riskId }) => riskId === value?.linkedRiskId)
  const groupedFormPermissions = groupedFormsPermissions?.find(
    ({ primaryFieldValueId }) =>
      primaryFieldValueId === value?.fieldValueId || primaryFieldValueId === value?.parentFieldValueId
  )

  const permissions = React.useMemo(() => {
    if (linkedFormPermissions) {
      const { permissions, risk } = linkedFormPermissions

      return {
        canModify: permissions.riskModify && risk.canUpdateFromSystemProcesses,
        canDelete: permissions.riskDelete && risk.canDeleteFromSystemProcesses,
        canArchive: permissions.riskArchive
      }
    }

    if (groupedFormPermissions) {
      const { permissions } = groupedFormPermissions

      return {
        canModify: permissions.modifyOwnedGroupedRisks,
        // TODO: change on riskDelete?
        canDelete: permissions.modifyOwnedGroupedRisks,
        canArchive: permissions.riskArchive
      }
    }

    if (risk) {
      const { permissions, canUpdateFromSystemProcesses } = risk
      const canModify = permissions.riskModify && canUpdateFromSystemProcesses

      return {
        canModify,
        canDelete: canModify,
        canArchive: permissions.riskArchive
      }
    }

    return {}
  }, [linkedFormPermissions, groupedFormPermissions, risk])

  const isRegisters = isRisksRegister || router.query.multiple
  const showLinkIcon = !isRegisters && !!linkedFormPermissions && linkedFormPermissions.risk.parentRiskId !== risk?.id

  const rowArchived = risk?.fieldValueMap?.find(({ fieldValueId }) => fieldValueId === value?.fieldValueId)?.archived
  const canArchiveRow = !rowArchived && riskTemplate.archiveOnDelete && permissions.canArchive

  const onDeleteConfirm = async () => {
    if (canArchiveRow) {
      try {
        if (!showArchivedForms) {
          deleteRow()
        }

        await archiveRow({
          variables: {
            ids: [value.fieldValueId],
            archived: true
          }
        })

        await riskRefetch()
      } catch (e) {
        console.log('[Row.onDeleteConfirm]: ', parseGraphQLErrorMessages(e))
      }
    } else {
      deleteRow()

      deleteFieldValues([value.fieldValueId])
    }
  }

  const unArchiveRow = async () => {
    try {
      await archiveRow({
        variables: {
          ids: [value.fieldValueId],
          archived: false
        }
      })

      await riskRefetch()
    } catch (e) {
      console.log('[Row.unArchive]: ', parseGraphQLErrorMessages(e))
    }
  }

  const handleDeleteClick = () => {
    showModal({
      width: 'sm',
      entity: canArchiveRow ? 'link' : 'row',
      action: canArchiveRow ? 'Archive' : 'Delete',
      handleConfirm: onDeleteConfirm
    })
  }

  return (
    <OuterWrapper $bg={bg}>
      <RowWrapper
        ref={ref}
        $mb={offset}
        $showLinkIcon={showLinkIcon}
        data-testid='risk-table-form-row'
        data-entity-id={value?.fieldValueId}>
        <FieldContent
          isForm
          field={field}
          name={fieldName}
          disabled={rowArchived || parentArchived}
          canModifyForm={permissions.canModify}
        />
        <HistoryButton
          field={field}
          riskId={value?.linkedRiskId}
          fieldValueId={linked ? value?.linkedFieldValueId : value?.fieldValueId}
        />
        {showLinkIcon && <LinkIcon />}
        {permissions.canModify && <InlineFunctions value={value} />}
        {!isRisksRegister && fieldLink.primary && (
          <ControlButtons
            field={field}
            canEdit={false}
            archived={rowArchived}
            canDelete={canArchiveRow ? permissions.canArchive : permissions.canDelete}
            onUnArchive={unArchiveRow}
            onDeleteClick={handleDeleteClick}
            linkedRiskId={value?.linkedRiskId}
            canUnarchive={rowArchived && !parentArchived}
          />
        )}
      </RowWrapper>
    </OuterWrapper>
  )
}

export default Row
